// Счётчик Яндекс.Метрики витрины (fd-analytics-05). Публичный запрос отдаёт
// counterId либо null. Тег инициализируется в корне SPA единожды; при null
// (или сбое запроса) ничего не вставляется и страница не ломается.
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { api } from "@/api";
import { metrikaHit, setMetrikaCounter } from "@/lib/metrika-ecommerce";

type YmFn = ((...args: unknown[]) => void) & { a?: unknown[]; l?: number };
type MetrikaWindow = Window & { ym?: YmFn };

let injected = false;

function injectMetrika(counterId: string): void {
	if (injected || typeof window === "undefined" || typeof document === "undefined") return;
	const w = window as MetrikaWindow;
	if (w.ym) {
		injected = true;
		return;
	}
	injected = true;

	// Стандартный загрузчик тега Яндекс.Метрики.
	const stub: YmFn = ((...args: unknown[]) => {
		(stub.a = stub.a || []).push(args);
	}) as YmFn;
	stub.l = Number(new Date());
	w.ym = w.ym || stub;

	const script = document.createElement("script");
	script.async = true;
	script.src = "https://mc.yandex.ru/metrika/tag.js";
	const first = document.getElementsByTagName("script")[0];
	first?.parentNode?.insertBefore(script, first);

	w.ym?.(Number(counterId), "init", {
		clickmap: true,
		trackLinks: true,
		accurateTrackBounce: true,
		// Электронная коммерция из `window.dataLayer` (lib/metrika-ecommerce).
		ecommerce: "dataLayer",
	});

	// noscript-фолбэк для клиентов без JS.
	const noscript = document.createElement("noscript");
	const div = document.createElement("div");
	const img = document.createElement("img");
	img.src = `https://mc.yandex.ru/watch/${counterId}`;
	img.style.position = "absolute";
	img.style.left = "-9999px";
	img.alt = "";
	div.appendChild(img);
	noscript.appendChild(div);
	document.body.appendChild(noscript);
}

export function useMetrika(): void {
	const { data, isError } = useQuery({
		queryKey: ["public", "metrika"],
		queryFn: () => api.public.metrika(),
		staleTime: Number.POSITIVE_INFINITY,
		retry: false,
		// Мягкая деградация: сбой запроса не должен ломать страницу.
		throwOnError: false,
	});

	useEffect(() => {
		if (data?.counterId) injectMetrika(data.counterId);
		// Номер счётчика известен (или его нет) - цели из очереди уходят или
		// выбрасываются.
		if (data) setMetrikaCounter(data.counterId);
		else if (isError) setMetrikaCounter(null);
	}, [data, isError]);
}

/**
 * Просмотры страниц при переходах внутри SPA (fd-analytics-05). Первую
 * страницу счётчик считает сам при инициализации; каждый следующий переход -
 * `hit` с адресом и предыдущей страницей. Заголовок берём после отрисовки:
 * страница ставит его в своём эффекте.
 */
export function useMetrikaPageHits(): void {
	const location = useLocation();
	const previous = useRef<string | null>(null);
	useEffect(() => {
		const url = `${location.pathname}${location.search}${location.hash}`;
		const referer = previous.current;
		previous.current = url;
		if (referer == null || referer === url) return;
		const origin = typeof window === "undefined" ? "" : window.location.origin;
		const timer = setTimeout(
			() => metrikaHit(`${origin}${url}`, `${origin}${referer}`, typeof document === "undefined" ? undefined : document.title),
			0,
		);
		return () => clearTimeout(timer);
	}, [location.pathname, location.search, location.hash]);
}
